import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountMenuButton } from 'features/layout/AccountMenuButton';
import { SponsorLogo } from 'features/layout/SponsorLogo';
import { useBlockFirstQuestionVital } from 'features/monitoring/useBlockFirstQuestionVital';
import { OnBackgroundText } from 'features/ui';
import { EmptyStateIllustration } from './EmptyStateIllustration';
import { delayedFadeInSx } from './fadeIn';

type EmptyStatePageProps = {
  title?: string;
  subtitle?: string;
  illustration?: ReactNode;
  action?: ReactNode;
  focusTitle?: boolean;
};

export const EmptyStatePage = ({
  title,
  subtitle,
  illustration,
  action,
  focusTitle,
}: EmptyStatePageProps) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useBlockFirstQuestionVital();

  useEffect(() => {
    if (focusTitle) titleRef.current?.focus();
  }, [focusTitle]);

  const displayTitle = title ?? t('QuestionnaireNotAvailableTitle');
  const displaySubtitle = subtitle ?? t('QuestionnaireNotAvailableSubtitle');

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        px: 3,
        py: 6,
        ...(focusTitle ? {} : delayedFadeInSx),
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 8, sm: 12 },
          left: { xs: 16, sm: 24 },
          height: 56,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <SponsorLogo />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 8, sm: 12 },
          right: { xs: 16, sm: 24 },
        }}
      >
        <AccountMenuButton />
      </Box>
      <Box
        sx={{
          width: '100%',
          maxWidth: 900,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
          gap: 4,
          alignItems: 'center',
        }}
      >
        {illustration ?? <EmptyStateIllustration />}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
            gap: 2,
          }}
        >
          <Typography
            ref={titleRef}
            tabIndex={focusTitle ? -1 : undefined}
            component="h1"
            variant="h3"
            sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            <OnBackgroundText>{displayTitle}</OnBackgroundText>
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 330, lineHeight: 1.7 }}
          >
            <OnBackgroundText multiline>{displaySubtitle}</OnBackgroundText>
          </Typography>
          {action}
        </Box>
      </Box>
    </Box>
  );
};
